import { calResize, getBaseIndexWithMode, transform, transformWithCenterPoint } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/resize';
import isLockAspect from '@editor/src/js/utils/lock-aspect';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import ElementInfoAdapter from '@src/slide/application/service/edit-action-services/element-info';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { IPoint, IResizableSize, ISizeMap } from './resizable.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { IMobileEditorAppServices } from '@src/slide/application/entry/toc/editor/mobile/mobile-editor-app-service.interface';

// iframe 元素设置最小宽高 20 * 20
const MIN_SIZE = { width: 20, height: 20 };

export default class ResizeLogic {
    private sizeInfoAtStartMap = new Map();
    private selectedModelArr: any[];

    /**
    * 记录所有选中元素的nextSize
    */
    public getSizeMap(sizeParams): ISizeMap | undefined {
        const selectedModelArr = this.getSelectedModelArr();
        if (selectedModelArr.length > 1) {
            const sizeMap = new Map();
            selectedModelArr.forEach((selectedModel) => {
                const { originalSize, nextSize } = this.getAllSize(sizeParams, selectedModel);
                sizeMap.set(selectedModel.key, {
                    originalSize,
                    nextSize,
                    elementModel: selectedModel,
                });
            });
            return sizeMap;
        }
    }

    /**
    * 获取元素的初始大小originalSize和元素变化后的大小nextSize
    */
    public getAllSize(sizeParams, selectedModel?) {
        const {
            e,
            isCroping,
            isIframe,
            resizeMode,
            rotate,
            startSize: mouseOnStartSize,
        } = sizeParams;
        const modelIsLockAspect = selectedModel
            ? isLockAspect(selectedModel.type, selectedModel.props)
            : sizeParams.modelIsLockAspect;
        let { flipAtStart, sizeAtStart, startSize, offsetX, offsetY } = sizeParams;
        if (selectedModel && this.sizeInfoAtStartMap?.get(selectedModel.key)) {
            ({ flipAtStart, sizeAtStart, startSize } = this.sizeInfoAtStartMap.get(selectedModel.key));
            // 对offsetX和offsetY按照当前鼠标作用元素和其他选中元素初始size比例进行缩放
            offsetX = offsetX * startSize.width / mouseOnStartSize.width;
            offsetY = offsetY * startSize.height / mouseOnStartSize.height;
        }

        const transformedRect = this.getRransformedRect(sizeAtStart, isCroping, rotate);
        const params = {
            transformedRect,
            flipAtStart,
            sizeAtStart,
            baseIndex: getBaseIndexWithMode(resizeMode, flipAtStart),
            rotate,
            offsetX,
            offsetY,
            e,
            isCroping,
            isIframe,
            minSize: isIframe ? MIN_SIZE : null,
        };
        const size = calResize(params);

        return {
            originalSize: startSize,
            nextSize: this.getNextSize(size, startSize, modelIsLockAspect, selectedModel),
        };
    }


    public getAppServices() {
        return (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices | IMobileEditorAppServices);
    }

    public getSelectionService() {
        return this.getAppServices().viewableServiceLayer?.selectionService;
    }

    public getRransformedRect = (sizeAtStart: IResizableSize, isCroping?: boolean, rotate?: number) => {
        const {
            left, top, width, height, centerX, centerY,
        } = sizeAtStart;
        // 计算初始状态旋转后的rect
        if (isCroping && this.isImageForegroundSizeValid()) {
            return transformWithCenterPoint({
                x: left,
                y: top,
                width,
                height,
                centerX,
                centerY,
            }, rotate);
        }
        return transform({
            x: left,
            y: top,
            width,
            height,
        }, rotate);
    };

    /**
    * 获取元素的start resize时的信息，包括翻转，大小，类型
    */
    public initSizeInfoAtStartMap() {
        const selectedModelArr = this.getSelectedModelArr();
        if (selectedModelArr.length > 1) {
            selectedModelArr.forEach((selectedModel) => {
                const flipAtStart = this.getFlip(selectedModel);
                const sizeAtStart = this.getSize(selectedModel);
                sizeAtStart.left = 0;
                sizeAtStart.top = 0;
                this.sizeInfoAtStartMap.set(selectedModel.key, {
                    flipAtStart,
                    sizeAtStart,
                    startSize: this.getSize(selectedModel),
                });
            });
        }
    }

    /**
     * 获取鼠标当前位置
     */
    public getEventCoordinates = (e): IPoint => ({
        x: e.touches ? e.touches[0].clientX : e.clientX,
        y: e.touches ? e.touches[0].clientY : e.clientY,
    });

    /**
    * 获取元素的在缩放过程中变化的size
    */
    private getNextSize(size, selectedStartSize: IResizableSize, modelIsLockAspect: boolean, selectedModel?) {
        let nextSize = size.targetSize;
        if (selectedModel) {
            if (isLockAspect(selectedModel.type, selectedModel.props)) {
                nextSize = size.targetSizeLockAspect;
            }
        } else {
            if (modelIsLockAspect) {
                nextSize = size.targetSizeLockAspect;
            }
        }
        // 还原左上顶点位置
        nextSize.left = selectedStartSize.left + nextSize.left;
        nextSize.top = selectedStartSize.top + nextSize.top;
        return nextSize;
    }

    /**
    * 获取所有选中的元素，包括组合元素
    */
    private getSelectedModelArr() {
        if (!this.selectedModelArr) {
            const selectedElements = this.getSelectionService()?.selectedElements;
            this.selectedModelArr = (selectedElements.selectedArrayForType(SelectedEleMgrType.SLIDE) || [])
                .concat(selectedElements.selectedArrayForType(SelectedEleMgrType.GROUP) || []);
        }
        return this.selectedModelArr;
    }

    /**
     * 图片前景图的大小是否有效，即不为0
     */
    private isImageForegroundSizeValid = (): boolean => {
        const model = this.getSelectionService()?.selectedElements.getCurrentModel();
        if (model.type !== UIModelType.IMAGE) return true;
        const { rw, rh } = (model as IPictureModel).getImageSize().getForegroundImageSize();
        if (rw === 0 || rh === 0) return false;
        return true;
    };

    /**
    * 获取元素的初始大小数据
    */
    private getSize = model => ElementInfoAdapter.sizeInfo(model);

    /**
        * 获取元素的初始翻转数据
        */
    private getFlip = model => ElementInfoAdapter.flipInfo(model);
}
